import { Box, Divider, Stack, Typography } from "@mui/material";
import { Switch } from "react-router-dom";

import AppIcon from "@/common/components/Icons/AppIcon";
import AppLink from "@/common/components/Link/AppLink";
import AppRoute from "@/common/components/Routing/AppRoute";
import { ROUTE_PATH } from "@/common/constants/routing";

import { DevColorInput } from "./Input/DevColorInput";
import { DevAlerts } from "./SubPages/DevAlerts";
import { DevAutocomplete } from "./SubPages/DevAutocompletes";
import { DevAvatars } from "./SubPages/DevAvatars";
import { DevBadges } from "./SubPages/DevBadges";
import { DevButtonGroups } from "./SubPages/DevButtonGroups";
import { DevButtons } from "./SubPages/DevButtons";
import { DevCards } from "./SubPages/DevCards";
import { DevChats } from "./SubPages/DevChats";
import { DevCheckboxes } from "./SubPages/DevCheckboxes";
import { DevChips } from "./SubPages/DevChips";
import { DevDataDisplay } from "./SubPages/DevDataDisplay";
import { DevDataGrid } from "./SubPages/DevDataGrid";
import { DevDataList } from "./SubPages/DevDataList";
import { DevDataTabular } from "./SubPages/DevDataTabular";
import { DevDividers } from "./SubPages/DevDividers";
import { DevFieldValue } from "./SubPages/DevFieldValue";
import { DevFiles } from "./SubPages/DevFiles";
import { DevFloatingActionButtons } from "./SubPages/DevFloatingActionButton";
import { DevFoldableBlock } from "./SubPages/DevFoldableBlock";
import { DevIcons } from "./SubPages/DevIcons";
import { DevInputs } from "./SubPages/DevInputs";
import { DevListItemGroup } from "./SubPages/DevListItemGroup";
import { DevMenus } from "./SubPages/DevMenus";
import { DevMisc } from "./SubPages/DevMisc";
import { DevModals } from "./SubPages/DevModals";
import { DevPagination } from "./SubPages/DevPagination";
import { DevPalette } from "./SubPages/DevPalette";
import { DevPaper } from "./SubPages/DevPaper";
import { DevPopovers } from "./SubPages/DevPopovers";
import { DevRadioGroups } from "./SubPages/DevRadioGroups";
import { DevRatings } from "./SubPages/DevRating";
import { DevRenderHelpers } from "./SubPages/DevRenderHelpers";
import { DevSelects } from "./SubPages/DevSelects";
import { DevSkeletons } from "./SubPages/DevSkeletons";
import { DevSliders } from "./SubPages/DevSliders";
import { DevSwitches } from "./SubPages/DevSwitches";
import { DevTabs } from "./SubPages/DevTabs";
import { DevTags } from "./SubPages/DevTags";
import { DevTempPlayground } from "./SubPages/DevTempPlayground";
import { DevText } from "./SubPages/DevText";
import { DevTextFields } from "./SubPages/DevTextFields";
import { DevTimelines } from "./SubPages/DevTimelines";
import { DevTooltips } from "./SubPages/DevTooltips";
import { DevTypography } from "./SubPages/DevTypography";

export function DevStyleguide() {
  return (
    <Stack sx={{ p: 3 }} direction={{ xxs: "column", md: "row" }} spacing={4}>
      {/* Nav */}
      <Stack direction='column' spacing={1}>
        <AppLink to={""} icon={<AppIcon of='code' inText />}>
          Index
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/palette`} icon={<AppIcon of='code' inText />}>
          Palette
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/typography`} icon={<AppIcon of='code' inText />}>
          Typography
        </AppLink>

        <Stack spacing={1}>
          <Typography variant='subtitle1'>Inputs</Typography>
          <AppLink
            to={`${ROUTE_PATH.DEV_STYLEGUIDE}/text-fields`}
            icon={<AppIcon of='code' inText />}
          >
            TextField
          </AppLink>
          <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/selects`} icon={<AppIcon of='code' inText />}>
            Select
          </AppLink>
          <AppLink
            to={`${ROUTE_PATH.DEV_STYLEGUIDE}/autocomplete`}
            icon={<AppIcon of='code' inText />}
          >
            Autocomplete
          </AppLink>
          <AppLink
            to={`${ROUTE_PATH.DEV_STYLEGUIDE}/checkboxes`}
            icon={<AppIcon of='code' inText />}
          >
            Checkbox
          </AppLink>
          <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/switches`} icon={<AppIcon of='code' inText />}>
            Switch
          </AppLink>
          <AppLink
            to={`${ROUTE_PATH.DEV_STYLEGUIDE}/color-input`}
            icon={<AppIcon of='code' inText />}
          >
            ColorInput
          </AppLink>
          <AppLink
            to={`${ROUTE_PATH.DEV_STYLEGUIDE}/custom-inputs`}
            icon={<AppIcon of='code' inText />}
          >
            Custom inputs
          </AppLink>
          <Divider />
        </Stack>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/paper`} icon={<AppIcon of='code' inText />}>
          Paper
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/dividers`} icon={<AppIcon of='code' inText />}>
          Dividers
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/cards`} icon={<AppIcon of='code' inText />}>
          Cards
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/alerts`} icon={<AppIcon of='code' inText />}>
          Alerts
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/avatars`} icon={<AppIcon of='code' inText />}>
          Avatars
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/buttons`} icon={<AppIcon of='code' inText />}>
          Buttons
        </AppLink>
        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/button-groups`}
          icon={<AppIcon of='code' inText />}
        >
          Button Groups
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/chips`} icon={<AppIcon of='code' inText />}>
          Chips
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/badges`} icon={<AppIcon of='code' inText />}>
          Badges
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/chats`} icon={<AppIcon of='code' inText />}>
          Chats
        </AppLink>

        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/data-display`}
          icon={<AppIcon of='code' inText />}
        >
          Data display
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/datalist`} icon={<AppIcon of='code' inText />}>
          DataList
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/datalist`} icon={<AppIcon of='code' inText />}>
          DataGrid
        </AppLink>
        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/datatabular`}
          icon={<AppIcon of='code' inText />}
        >
          DataTabular
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/icons`} icon={<AppIcon of='code' inText />}>
          Icons
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/files`} icon={<AppIcon of='code' inText />}>
          Files
        </AppLink>
        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/floating-action-buttons`}
          icon={<AppIcon of='code' inText />}
        >
          Floating Action Buttons
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/menus`} icon={<AppIcon of='code' inText />}>
          Menus
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/modals`} icon={<AppIcon of='code' inText />}>
          Modals
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/pagination`} icon={<AppIcon of='code' inText />}>
          Pagination
        </AppLink>
        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/radio-groups`}
          icon={<AppIcon of='code' inText />}
        >
          Radio Groups
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/ratings`} icon={<AppIcon of='code' inText />}>
          Ratings
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/tags`} icon={<AppIcon of='code' inText />}>
          Tags
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/text`} icon={<AppIcon of='code' inText />}>
          Text
        </AppLink>

        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/render-helpers`}
          icon={<AppIcon of='code' inText />}
        >
          Render helpers
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/sliders`} icon={<AppIcon of='code' inText />}>
          Sliders
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/skeletons`} icon={<AppIcon of='code' inText />}>
          Skeletons
        </AppLink>

        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/tooltips`} icon={<AppIcon of='code' inText />}>
          Tooltips
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/popovers`} icon={<AppIcon of='code' inText />}>
          Popovers
        </AppLink>
        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/field-value`}
          icon={<AppIcon of='code' inText />}
        >
          FieldValue
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/tabs`} icon={<AppIcon of='code' inText />}>
          Tabs
        </AppLink>
        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/foldable-block`}
          icon={<AppIcon of='code' inText />}
        >
          FoldableBlock
        </AppLink>
        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/list-item-group`}
          icon={<AppIcon of='code' inText />}
        >
          ListItemGroup
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/timelines`} icon={<AppIcon of='code' inText />}>
          Timelines
        </AppLink>
        <AppLink to={`${ROUTE_PATH.DEV_STYLEGUIDE}/misc`} icon={<AppIcon of='code' inText />}>
          Misc
        </AppLink>
        <AppLink
          to={`${ROUTE_PATH.DEV_STYLEGUIDE}/temp-playground`}
          icon={<AppIcon of='code' inText />}
        >
          Temp playground
        </AppLink>
      </Stack>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component='div' variant='h1' sx={{ mb: 2 }}>
          Styleguide
        </Typography>

        <Switch>
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/palette`}
            render={() => {
              return <DevPalette />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/avatars`}
            render={() => {
              return <DevAvatars />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/autocomplete`}
            render={() => {
              return <DevAutocomplete />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/icons`}
            render={() => {
              return <DevIcons />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/buttons`}
            render={() => {
              return <DevButtons />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/button-groups`}
            render={() => {
              return <DevButtonGroups />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/chips`}
            render={() => {
              return <DevChips />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/badges`}
            render={() => {
              return <DevBadges />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/color-input`}
            render={() => {
              return <DevColorInput />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/custom-inputs`}
            render={() => {
              return <DevInputs />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/datalist`}
            render={() => {
              return <DevDataList />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/datagrid`}
            render={() => {
              return <DevDataGrid />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/datatabular`}
            render={() => {
              return <DevDataTabular />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/files`}
            render={() => {
              return <DevFiles />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/floating-action-buttons`}
            render={() => {
              return <DevFloatingActionButtons />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/chats`}
            render={() => {
              return <DevChats />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/checkboxes`}
            render={() => {
              return <DevCheckboxes />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/menus`}
            render={() => {
              return <DevMenus />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/modals`}
            render={() => {
              return <DevModals />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/pagination`}
            render={() => {
              return <DevPagination />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/radio-groups`}
            render={() => {
              return <DevRadioGroups />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/ratings`}
            render={() => {
              return <DevRatings />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/paper`}
            render={() => {
              return <DevPaper />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/dividers`}
            render={() => {
              return <DevDividers />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/cards`}
            render={() => {
              return <DevCards />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/alerts`}
            render={() => {
              return <DevAlerts />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/data-display`}
            render={() => {
              return <DevDataDisplay />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/tags`}
            render={() => {
              return <DevTags />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/typography`}
            render={() => {
              return <DevTypography />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/text`}
            render={() => {
              return <DevText />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/text-fields`}
            render={() => {
              return <DevTextFields />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/render-helpers`}
            render={() => {
              return <DevRenderHelpers />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/selects`}
            render={() => {
              return <DevSelects />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/sliders`}
            render={() => {
              return <DevSliders />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/skeletons`}
            render={() => {
              return <DevSkeletons />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/switches`}
            render={() => {
              return <DevSwitches />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/tooltips`}
            render={() => {
              return <DevTooltips />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/popovers`}
            render={() => {
              return <DevPopovers />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/field-value`}
            render={() => {
              return <DevFieldValue />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/tabs`}
            render={() => {
              return <DevTabs />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/foldable-block`}
            render={() => {
              return <DevFoldableBlock />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/list-item-group`}
            render={() => {
              return <DevListItemGroup />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/timelines`}
            render={() => {
              return <DevTimelines />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/misc`}
            render={() => {
              return <DevMisc />;
            }}
          />
          <AppRoute
            exact
            path={`${ROUTE_PATH.DEV_STYLEGUIDE}/temp-playground`}
            render={() => {
              return <DevTempPlayground />;
            }}
          />
        </Switch>
      </Box>
    </Stack>
  );
}
